import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, RefreshCw, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, paid, failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempt) => {
    const maxAttempts = 10;
    if (attempt >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sid}`);
      if (!response.ok) throw new Error('Erreur vérification');

      const data = await response.json();
      setAttempts(attempt + 1);

      if (data.payment_status === 'paid') {
        setStatus('paid');
        toast.success('Paiement confirmé !');
        return;
      }

      if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempt + 1), 2000);
    } catch (error) {
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempt + 1), 2000);
      } else {
        setStatus('failed');
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="h-14 sm:h-16 border-b bg-background/95 backdrop-blur flex items-center px-4 sm:px-6">
          <h1 className="text-xl sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Litige-Protect
          </h1>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-4">
            <RefreshCw className="w-12 h-12 text-accent animate-spin mx-auto" />
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Vérification du paiement...
            </h2>
            <p className="text-foreground/60">Veuillez patienter, nous confirmons votre paiement</p>
            <p className="text-xs text-foreground/40">Tentative {attempts + 1}/10</p>
          </div>
        </main>
      </div>
    );
  }

  if (status === 'paid') {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="h-14 sm:h-16 border-b bg-background/95 backdrop-blur flex items-center px-4 sm:px-6">
          <h1 className="text-xl sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Litige-Protect
          </h1>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-6 max-w-md">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-2">
              <CheckCircle className="w-10 h-10 text-green-600" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Paiement confirmé !
            </h2>
            <p className="text-foreground/70">
              Votre abonnement Litige-Protect est maintenant actif. Connectez-vous pour accéder à votre tableau de bord.
            </p>
            <button
              data-testid="go-to-login-btn"
              onClick={() => navigate('/admin')}
              className="w-full h-12 sm:h-14 bg-accent text-accent-foreground text-base sm:text-lg font-medium rounded-md hover:bg-accent/90 transition-all"
            >
              Se connecter
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Failed
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-14 sm:h-16 border-b bg-background/95 backdrop-blur flex items-center px-4 sm:px-6">
        <h1 className="text-xl sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Litige-Protect
        </h1>
      </header>
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-2">
            <XCircle className="w-10 h-10 text-red-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Paiement non confirmé
          </h2>
          <p className="text-foreground/70">
            Le paiement n'a pas pu être vérifié. Veuillez réessayer ou contacter le support.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/admin/register')}
              className="w-full h-12 bg-accent text-accent-foreground font-medium rounded-md hover:bg-accent/90 transition-all"
            >
              Réessayer l'inscription
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full h-10 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 text-sm"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
